/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 숫자포맷 클래스를 정의한다.
 *
 * @file /src/utils/FormatNumber.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2025. 8. 11.
 */
export class FormatNumber {
    value: string;
    locale: string;
    groupSeparator: string;
    decimalSeparator: string;

    /**
     * 숫자포맷 클래스를 정의한다.
     *
     * @param value - 숫자
     */
    constructor(value: number | string, locale: string = null) {
        this.locale = locale ?? document.querySelector('html').getAttribute('lang') ?? 'ko';
        const parts = (1234.5).toLocaleString(this.locale).match(/(\D+)/g);
        this.groupSeparator = parts != null ? parts[0] : ',';
        this.decimalSeparator = parts != null ? parts[1] : '.';

        this.value = this.toString(value);
    }

    /**
     * 값을 변경한다.
     *
     * @param value - 숫자
     */
    setValue(value: number | string): FormatNumber {
        this.value = this.toString(value);
        return this;
    }

    /**
     * 숫자를 문자열로 변경한다.
     *
     * @param number - 변경할 값
     * @returns 숫자문자열
     */
    toString(number: number | string): string {
        if (typeof number == 'number') {
            return number.toString();
        }

        return this.unformat(number);
    }

    /**
     * 숫자를 지역에 따라 포맷팅한다. ("1234.56 → 1,234.56")
     *
     * @param value - 포맷팅할 숫자 문자열 (NULL 인 경우 현재값)
     * @returns 포맷팅된 문자열
     */
    format(value: string = null): string {
        value ??= this.value;
        const number = parseFloat(value);
        if (isNaN(number) == true) {
            return '';
        }

        return number.toLocaleString(this.locale, { maximumFractionDigits: 20 });
    }

    /**
     * 포맷을 제거한 숫자문자열을 가져온다. ("1,234.56" → "1234.56")
     *
     * @param value - 포맷을 제거할 숫자 문자열 (NULL 인 경우 현재값)
     * @returns 포맷이 제거된 숫자문자열
     */
    unformat(value: string = null): string {
        value ??= this.value;
        let unformatted = value.split(this.groupSeparator).join('');
        unformatted = unformatted.split(this.decimalSeparator).join('.');

        // 여러 개의 소수점 방지
        const parts = unformatted.split('.');
        if (parts.length > 2) {
            unformatted = parts.shift() + '.' + parts.join('');
        }
        return unformatted;
    }

    /**
     * 숫자문자열에서 숫자 갯수를 가져온다.
     *
     * @param value - 숫자 갯수를 가져올 숫자 문자열 (NULL 인 경우 현재값)
     * @returns 숫자갯수
     */
    digits(value: string | number = null): number {
        value = this.toString(value ?? this.value);
        return value.replace(/[^\d]/g, '').length;
    }

    /**
     * 숫자 인덱스에서 커서위치를 가져온다.
     * (ex: “1,234” 에서 3번째 숫자 = 위치 4)
     *
     * @param digitIndex - 숫자인덱스
     * @param value - 커서 오프셋을 가져올 숫자문자열 (NULL 인 경우 현재값)
     * @returns 커서위치
     */
    getOffsetFromDigitIndex(digitIndex: number, value: string | number = null): number {
        const formatted = this.format(this.toString(value ?? this.value));

        if (digitIndex == 0) {
            return 0;
        }

        let count = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i]) == true) {
                count++;
                if (count == digitIndex) {
                    return i + 1;
                }
            }
        }
        return formatted.length;
    }

    /**
     * 숫자문자열에서 숫자로 변경한다.
     *
     * @param value - 숫자로 변경할 숫자 문자열 (NULL 인 경우 현재값)
     * @returns 숫자
     */
    toNumber(value: string | number = null): number {
        value = this.toString(value);
        return parseFloat(value);
    }
}
